import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { pedirReserva, fechaMexico, sumarDias, telefonoValido, correoValido } from "../lib/reservas-publicas";
import "./Reservar.css";

export const TEXTOS = {
  cargando: "Cargando…", titulo: "Reservar cita", noDisponible: "Esta página de reservas no está disponible.",
  error: "No se pudo completar la reserva. Intenta de nuevo.", profesional: "Con quién", servicio: "Servicio",
  dia: "Día", hoy: "hoy", hora: "Hora", eligeDia: "Elige un día para ver los horarios.",
  cargandoHoras: "Cargando horarios…", errorHoras: "No se pudieron cargar los horarios.", reintentar: "Reintentar",
  sinHoras: "No hay horarios disponibles este día. Prueba otro día.", datos: "Tus datos", nombre: "Nombre",
  correo: "Correo", telefono: "Teléfono", nacimiento: "Fecha de nacimiento", resumen: "Resumen", reservar: "Reservar",
  reservando: "Reservando…", privacidad: "Al reservar aceptas el aviso de privacidad.", revisar: "Revisa tus datos.",
  ocupado: "Ese horario se acaba de ocupar. Elige otro.", limite: "Hiciste demasiados intentos. Intenta más tarde.",
  listo: "¡Listo! Tu cita quedó registrada.", confirmacion: "Te enviamos un correo a {correo} con los datos de tu cita.",
  otra: "Reservar otra cita", marca: "Reservas con Surit.ai", volver: "Volver", continuar: "Continuar",
  menor: "Para agendar a un menor de edad, comunícate directamente con la clínica.",
  anticipo: "Esta cita requiere un anticipo de $X antes del {fecha}. La clínica te indicará cómo pagarlo.",
  pendiente: "Pendiente", confirmado: "Confirmado",
};

const inicial = { nombre: "", telefono: "", fecha_nacimiento: "", correo: "" };
const dinero = (n) => new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n));
// Fechas civiles: nunca convertirlas a la zona horaria del navegador.
const fechaLabel = (f, opciones = {}) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(f || "")) return "";
  const date = new Date(`${f}T12:00:00Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== f) return "";
  return date.toLocaleDateString("es-MX", { timeZone: "UTC", day: "numeric", month: "long", ...opciones });
};
const fechaLarga = (f) => fechaLabel(f, { weekday: "long" }).replace(",", "");
const fechaCita = (f, h) => `${fechaLarga(f)}${h ? `, ${h}` : ""}`;
const moverMes = (mes, salto) => {
  const date = new Date(`${mes}T12:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + salto);
  return date.toISOString().slice(0, 10);
};

function Calendario({ hoy, fecha, disabled, onChange }) {
  const [mes, setMes] = useState(`${hoy.slice(0, 7)}-01`);
  const [foco, setFoco] = useState(hoy);
  const dias = useRef({});
  const enfocar = useRef(false);
  const limite = sumarDias(hoy, 90);
  const mesInicial = `${hoy.slice(0, 7)}-01`;
  const mesFinal = `${limite.slice(0, 7)}-01`;
  const siguiente = moverMes(mes, 1);
  const cantidad = Number(sumarDias(siguiente, -1).slice(8));
  const inicioSemana = (new Date(`${mes}T12:00:00Z`).getUTCDay() + 6) % 7;
  const mesLabel = (f) => fechaLabel(f, { day: undefined, year: "numeric" }).replace(" de ", " ");

  useEffect(() => {
    if (enfocar.current) { dias.current[foco]?.focus(); enfocar.current = false; }
  }, [foco, mes]);

  const navegar = (nuevoMes) => {
    setMes(nuevoMes);
    setFoco(nuevoMes < hoy ? hoy : nuevoMes);
  };
  const teclado = (e, f) => {
    const semana = (new Date(`${f}T12:00:00Z`).getUTCDay() + 6) % 7;
    const saltos = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -semana, End: 6 - semana };
    if (!(e.key in saltos)) return;
    e.preventDefault();
    const destino = sumarDias(f, saltos[e.key]);
    if (destino === f || destino < hoy || destino > limite) return;
    enfocar.current = true;
    setFoco(destino); setMes(`${destino.slice(0, 7)}-01`);
  };

  return <div className="reserva-calendario">
    <div className="reserva-mes">
      <button type="button" aria-label={mesLabel(moverMes(mes, -1))} disabled={disabled || mes <= mesInicial}
        onClick={() => navegar(moverMes(mes, -1))}>‹</button>
      <strong aria-live="polite" aria-atomic="true">{mesLabel(mes)}</strong>
      <button type="button" aria-label={mesLabel(siguiente)} disabled={disabled || mes >= mesFinal}
        onClick={() => navegar(siguiente)}>›</button>
    </div>
    <div className="reserva-semana" aria-hidden="true">{["L", "M", "M", "J", "V", "S", "D"].map((d, i) => <span key={i}>{d}</span>)}</div>
    <div className="reserva-dias">
      {Array.from({ length: inicioSemana }, (_, i) => <span key={`vacio-${i}`} aria-hidden="true" />)}
      {Array.from({ length: cantidad }, (_, i) => sumarDias(mes, i)).map((f) => {
        const noElegible = disabled || f < hoy || f > limite;
        return <button key={f} type="button" ref={(el) => { dias.current[f] = el; }} data-fecha={f}
          aria-label={fechaLarga(f)} aria-pressed={fecha === f} aria-disabled={noElegible} disabled={noElegible}
          aria-current={f === hoy ? "date" : undefined} title={f === hoy ? TEXTOS.hoy : undefined}
          tabIndex={f === foco ? 0 : -1} onFocus={() => setFoco(f)} onKeyDown={(e) => teclado(e, f)}
          onClick={() => { if (fecha !== f) onChange(f); }}>{Number(f.slice(8))}</button>;
      })}
    </div>
  </div>;
}

function Opcion({ grupo, valor, seleccionada, onChange, children, className = "", label }) {
  return <label className={`reserva-opcion ${className}`}>
    <input type="radio" name={grupo} value={valor} checked={seleccionada} onChange={onChange} aria-label={label} />
    <span className="reserva-opcion-cuerpo">{children}</span>
  </label>;
}

export default function Reservar() {
  const { slug } = useParams();
  const [fase, setFase] = useState("cargando");
  const [info, setInfo] = useState(null);
  const [profesionales, setProfesionales] = useState([]);
  const [profesional, setProfesional] = useState(null);
  const [servicio, setServicio] = useState(null);
  const [hoy, setHoy] = useState(fechaMexico);
  const [fecha, setFecha] = useState("");
  const [hora, setHora] = useState("");
  const [horas, setHoras] = useState([]);
  const [estadoHoras, setEstadoHoras] = useState("inicial");
  const [datos, setDatos] = useState(inicial);
  const [mensaje, setMensaje] = useState("");
  const [resultado, setResultado] = useState(null);
  const [recarga, setRecarga] = useState(0);
  const [recargaHoras, setRecargaHoras] = useState(0);
  const enviando = useRef(false);
  const intento = useRef(null);
  const notificacion = useRef(null);

  useEffect(() => {
    if (fase === "exito" || mensaje) notificacion.current?.focus();
  }, [fase, mensaje]);

  useEffect(() => {
    const controller = new AbortController();
    setFase("cargando"); setMensaje(""); setProfesional(null); setServicio(null); setFecha(""); setHora("");
    Promise.all([pedirReserva(slug, "info", null, controller.signal), pedirReserva(slug, "profesionales", null, controller.signal)])
      .then(([i, p]) => {
        if (controller.signal.aborted) return;
        setInfo(i.data); setHoy(i.hoy || fechaMexico()); setProfesionales(p.data);
        if (p.data.length === 1) { setProfesional(p.data[0]); setFase("servicio"); }
        else setFase("profesional");
      }).catch((e) => { if (!controller.signal.aborted) { setFase(e.status === 404 ? "noDisponible" : "error"); setMensaje(e.status === 429 ? TEXTOS.limite : ""); } });
    return () => controller.abort();
  }, [slug, recarga]);

  useEffect(() => {
    if (!fecha || !profesional || !servicio) return undefined;
    const controller = new AbortController();
    setEstadoHoras("cargando"); setHora("");
    const params = new URLSearchParams({ profesional_id: profesional.id, servicio_id: servicio.id, fecha });
    pedirReserva(slug, `horarios?${params}`, null, controller.signal).then(({ data }) => {
      if (!controller.signal.aborted) { setHoras(data); setEstadoHoras("listo"); }
    }).catch((e) => {
      if (!controller.signal.aborted) {
        if (e.status === 404) setFase("noDisponible");
        setEstadoHoras("error");
        if (e.status === 429) setMensaje(TEXTOS.limite);
      }
    });
    return () => controller.abort();
  }, [slug, fecha, profesional, servicio, recargaHoras]);

  const cambiar = (paso) => { intento.current = null; setMensaje(""); setFase(paso); };
  const reservar = async (e) => {
    e.preventDefault();
    if (enviando.current) return;
    const d = Object.fromEntries(Object.entries(datos).map(([k, v]) => [k, v.trim()]));
    if (!profesional || !servicio || !fecha || !hora || !d.nombre || d.nombre.length > 150 || !telefonoValido(d.telefono) || !correoValido(d.correo)
        || !/^\d{4}-\d{2}-\d{2}$/.test(d.fecha_nacimiento) || d.fecha_nacimiento < "1900-01-01" || d.fecha_nacimiento > hoy
        || !fechaLabel(d.fecha_nacimiento)) { setFase("datos"); setMensaje(TEXTOS.revisar); return; }
    setDatos(d);
    enviando.current = true; setFase("enviando"); setMensaje("");
    // Un intento conserva la llave si se pierde la respuesta; editar inicia otro intento.
    intento.current = intento.current || crypto.randomUUID();
    try {
      const { data } = await pedirReserva(slug, "reservar", {
        profesional_id: profesional.id, servicio_id: servicio.id, fecha, hora, ...d, clave_idempotencia: intento.current,
      });
      setResultado(data); setFase("exito");
    } catch (e) {
      if (e.status === 409) {
        intento.current = null; setHora(""); setRecargaHoras((n) => n + 1); setFase("hora"); setMensaje(TEXTOS.ocupado);
      } else if (e.status === 404) setFase("noDisponible");
      else {
        if (e.status) intento.current = null;
        setFase(e.status === 422 ? "datos" : "resumen");
        setMensaje(({ 422: TEXTOS.revisar, 429: TEXTOS.limite, 403: TEXTOS.menor })[e.status] || TEXTOS.error);
      }
    } finally { enviando.current = false; }
  };

  const formulario = ["profesional", "servicio", "dia", "hora", "datos", "resumen", "enviando"].includes(fase);
  const bloqueado = fase === "enviando";
  const avisoAnticipo = (a) => TEXTOS.anticipo.replace("$X", `$${dinero(a.monto)}`).replace("{fecha}", fechaLabel(a.limite));
  const detalle = [
    [TEXTOS.profesional, profesional?.nombre], [TEXTOS.servicio, servicio?.nombre],
    [TEXTOS.dia, fecha && fechaCita(fecha, hora)], [TEXTOS.nombre, datos.nombre], [TEXTOS.telefono, datos.telefono],
    [TEXTOS.nacimiento, fechaLabel(datos.fecha_nacimiento, { year: "numeric" })], [TEXTOS.correo, datos.correo],
  ];

  return <main className="reserva-publica" data-fase={fase}>
    <div className="reserva-contenido">
      <header className="reserva-clinica">
        <p>{TEXTOS.titulo}</p>
        {info && <h1>{info.nombre}</h1>}
      </header>
      <div aria-live="polite" aria-atomic="true" className="reserva-mensajes">
        {fase === "cargando" && <p role="status">{TEXTOS.cargando}</p>}
        {fase === "noDisponible" && <p>{TEXTOS.noDisponible}</p>}
        {mensaje && <p role="alert" tabIndex={-1} ref={notificacion}>{mensaje}</p>}
        {fase === "error" && <>{!mensaje && <p role="alert">{TEXTOS.error}</p>}<button type="button" onClick={() => setRecarga((n) => n + 1)}>{TEXTOS.reintentar}</button></>}
      </div>
      {formulario && <form onSubmit={reservar} noValidate>
        {profesionales.length > 1 && <fieldset className="reserva-tarjeta" disabled={bloqueado}>
          <legend>{TEXTOS.profesional}</legend>
          <div className="reserva-opciones">{profesionales.map((p) => <Opcion key={p.id} grupo="profesional" valor={p.id} seleccionada={profesional?.id === p.id}
            onChange={() => { setProfesional(p); setServicio(null); setFecha(""); setHora(""); setHoras([]); setEstadoHoras("inicial"); cambiar("servicio"); }}>
            <strong>{p.nombre}</strong><small>{p.especialidad}</small>
          </Opcion>)}</div>
        </fieldset>}
        <fieldset className="reserva-tarjeta" disabled={bloqueado}>
          <legend>{TEXTOS.servicio}</legend>
          <div className="reserva-opciones">{profesional?.servicios.map((s) => <Opcion key={s.id} grupo="servicio" valor={s.id} seleccionada={servicio?.id === s.id}
            onChange={() => { setServicio(s); setFecha(""); setHora(""); setHoras([]); setEstadoHoras("inicial"); cambiar("dia"); }}>
            <strong>{s.nombre}</strong>{s.descripcion && <small>{s.descripcion}</small>}<span className="reserva-precio">{s.duracion_min} min · ${dinero(s.precio)}</span>
          </Opcion>)}</div>
        </fieldset>
        <fieldset className="reserva-tarjeta reserva-tarjeta-calendario" disabled={bloqueado || !servicio}>
          <legend>{TEXTOS.dia}</legend>
          <Calendario key={hoy} hoy={hoy} fecha={fecha} disabled={bloqueado || !servicio}
            onChange={(f) => { setFecha(f); setHora(""); setHoras([]); setEstadoHoras("cargando"); cambiar("hora"); }} />
        </fieldset>
        <fieldset className="reserva-tarjeta" disabled={bloqueado}>
          <legend>{TEXTOS.hora}</legend>
          <div aria-live="polite">
            {!fecha && <p>{TEXTOS.eligeDia}</p>}
            {fecha && estadoHoras === "cargando" && <p role="status">{TEXTOS.cargandoHoras}</p>}
            {fecha && estadoHoras === "error" && <><p>{TEXTOS.errorHoras}</p><button type="button" onClick={() => setRecargaHoras((n) => n + 1)}>{TEXTOS.reintentar}</button></>}
            {fecha && estadoHoras === "listo" && (horas.length ? <div className="reserva-horas">{horas.map((h) => <Opcion key={h} grupo="hora" valor={h} seleccionada={hora === h} className="reserva-chip"
              onChange={() => { setHora(h); cambiar("datos"); }}><strong>{h}</strong></Opcion>)}</div> : <p>{TEXTOS.sinHoras}</p>)}
          </div>
        </fieldset>
        <fieldset className="reserva-tarjeta" disabled={bloqueado}>
          <legend>{TEXTOS.datos}</legend>
          <div className="reserva-campos">{Object.entries({ nombre: TEXTOS.nombre, correo: TEXTOS.correo, telefono: TEXTOS.telefono, fecha_nacimiento: TEXTOS.nacimiento }).map(([key, label]) => <label key={key}>{label}<input name={key} required type={({ telefono: "tel", fecha_nacimiento: "date", correo: "email" })[key] || "text"} maxLength={({ nombre: 150, telefono: 40, correo: 254 })[key]} min={key === "fecha_nacimiento" ? "1900-01-01" : undefined} max={key === "fecha_nacimiento" ? hoy : undefined} autoComplete={({ nombre: "name", telefono: "tel", fecha_nacimiento: "bday", correo: "email" })[key]} value={datos[key]}
            onChange={(e) => { setDatos({ ...datos, [key]: e.target.value }); cambiar("datos"); }} /></label>)}</div>
        </fieldset>
        <fieldset className="reserva-tarjeta reserva-resumen" disabled={bloqueado}>
          <legend>{TEXTOS.resumen}</legend>
          <dl>{detalle.filter(([, valor]) => valor).map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl>
          {servicio?.requiere_anticipo && fecha && <p className="reserva-anticipo">{avisoAnticipo({ monto: servicio.anticipo_sugerido, limite: sumarDias(fecha, -1) < hoy ? hoy : sumarDias(fecha, -1) })}</p>}
          <p className="reserva-privacidad"><a href="https://surit.ai/privacidad" target="_blank" rel="noreferrer">{TEXTOS.privacidad}</a></p>
          <button className="reserva-principal" type="submit" disabled={bloqueado || !hora}>{bloqueado ? TEXTOS.reservando : TEXTOS.reservar}</button>
        </fieldset>
      </form>}
      {fase === "exito" && <section className="reserva-exito" aria-labelledby="reserva-exito-titulo">
        <h2 id="reserva-exito-titulo" tabIndex={-1} ref={notificacion}>{TEXTOS.listo}</h2>
        <span className={`reserva-estado ${resultado.estado === "Confirmado" ? "reserva-confirmado" : "reserva-pendiente"}`}>{resultado.estado === "Confirmado" ? TEXTOS.confirmado : TEXTOS.pendiente}</span>
        <div className="reserva-tarjeta reserva-cita"><dl>{[[TEXTOS.profesional, resultado.profesional], [TEXTOS.servicio, resultado.servicio], [TEXTOS.dia, fechaCita(resultado.fecha, resultado.hora)]].map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl></div>
        <p>{TEXTOS.confirmacion.replace("{correo}", datos.correo)}</p>
        {resultado.anticipo && <p className="reserva-anticipo">{avisoAnticipo(resultado.anticipo)}</p>}
        <button type="button" onClick={() => { setDatos(inicial); intento.current = null; setRecarga((n) => n + 1); }}>{TEXTOS.otra}</button>
      </section>}
      <footer><a href="https://surit.ai" target="_blank" rel="noreferrer">{TEXTOS.marca}</a></footer>
    </div>
  </main>;
}
